import React from 'react'
import { useMutation } from 'react-query'
import axios, { AxiosError } from 'axios'
import { getAppConfigSetting } from '../utils'
import { SENTINEL_API } from './constants'
import { Alert, useAlert } from '@apex/component-library'
import { BeneficiaryDetails, IraDistribution } from '@Types'
import { useToast } from '@chakra-ui/react'
import { DeliveryMethodTypes } from '../containers/CheckTransfers/Types'
import { SERVICE_UNAVAILABLE_TOAST, TOTAL_TRANSFER_SUCCESS_TOAST, TRANSFER_SUCCESS_TOAST } from '../constants'

export interface CheckWithdrawalRequest {
  externalId: string
  account: string
  deliveryMethod: DeliveryMethodTypes
  beneficiaryDetails: BeneficiaryDetails
  memos: (string | null)[]
  amount?: number
  isFullDisbursement?: boolean
  fullDisbursement?: boolean
  iraDistributionDetails?: IraDistribution
  iraDistribution?: IraDistribution
}

interface CheckWithdrawalResponse {
  id?: string
  externalId?: string
  status?: string
  account?: string
  amount?: number
  deliveryMethod: DeliveryMethodTypes
  beneficiaryDetails?: BeneficiaryDetails
  iraDistributionDetails?: IraDistribution
  memos?: string[]
}

const postCheckWithdrawal = async (payload: CheckWithdrawalRequest): Promise<CheckWithdrawalResponse> => {
  const checkWithdrawalURL = `${getAppConfigSetting('apiUrl')}${SENTINEL_API}/checks/withdrawals`

  const { data } = await axios.post(checkWithdrawalURL, payload, {
    withCredentials: true
  })

  return data
}

type Options = { onSuccess: () => void; onFail: () => void; amountAvailable: number }

export const useCreateCheckWithdrawal = ({ onSuccess, onFail, amountAvailable }: Options) => {
  const toast = useToast()
  const { setAlert } = useAlert()

  return useMutation(postCheckWithdrawal, {
    onError: (error: AxiosError) => {
      if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
        toast({
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
          title: 'The request failed.',
          description: `${error.response.data.errorDescription}`,
          status: 'error',
          duration: null,
          render: ({ onClose, id }) => (
            <Alert
              sx={{
                width: 340,
                color: 'black',
                bg: 'white',
                boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
              }}
              direction='column'
              variant='left-accent'
              title='The request failed.'
              description={`${error?.response?.data.errorDescription}`}
              status='error'
              isClosable
              dismiss={onClose}
              id={id as string}
            />
          )
        })
      } else {
        setAlert({
          direction: 'row',
          variant: 'left-accent',
          title: 'System Unavailable',
          description: SERVICE_UNAVAILABLE_TOAST,
          status: 'error',
          isClosable: true
        })
      }
      onFail()
    },
    onSuccess: (data, variables, context) => {
      const description =
        variables.isFullDisbursement !== undefined || amountAvailable === variables.amount
          ? TOTAL_TRANSFER_SUCCESS_TOAST
          : TRANSFER_SUCCESS_TOAST
      toast({
        duration: 5000,
        position: 'top-right',
        title: 'Success!',
        variant: 'left-accent',
        description: description,
        status: 'success',
        isClosable: true,
        render: ({ onClose, id }) => (
          <Alert
            sx={{
              width: 340,
              color: 'black',
              bg: 'white',
              boxShadow: '0px 0px 5px 3px rgba(153,153,153,0.5)'
            }}
            direction='column'
            variant='left-accent'
            title='Success!'
            description={description}
            status='success'
            isClosable
            dismiss={onClose}
            id={id as string}
          />
        )
      })
      onSuccess()
    }
  })
}
